import { lazy, Suspense, type ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useSearchParams } from 'react-router';
import { AppShell } from '@/components/app-shell';
import { authClient } from '@/lib/auth-client';
import { nextQuery, safeNextPath } from '@/lib/next-path';
import { DashboardPage } from '@/routes/dashboard';
import { InvitationPage } from '@/routes/invitation';
import { LandingPage } from '@/routes/landing';
import { LoginPage } from '@/routes/login';
import { RegisterPage } from '@/routes/register';
import { ResetPasswordPage } from '@/routes/reset-password';

// Pagina's buiten de eerste weergave apart laden (kleinere bundel op mobiel).
const page = <K extends string>(load: () => Promise<Record<K, () => ReactNode>>, name: K) =>
  lazy(() => load().then((m) => ({ default: m[name] })));
const ClubPage = page(() => import('@/routes/club'), 'ClubPage');
const ExercisesPage = page(() => import('@/routes/exercises'), 'ExercisesPage');
const ExerciseDetailPage = page(() => import('@/routes/exercise-detail'), 'ExerciseDetailPage');
const ExerciseNewPage = page(() => import('@/routes/exercise-edit'), 'ExerciseNewPage');
const ExerciseEditPage = page(() => import('@/routes/exercise-edit'), 'ExerciseEditPage');
const ExerciseVideoPage = page(() => import('@/routes/exercise-video'), 'ExerciseVideoPage');
const ModerationPage = page(() => import('@/routes/moderation'), 'ModerationPage');
const ModerationReviewPage = page(() => import('@/routes/moderation'), 'ModerationReviewPage');
const TrainingsPage = page(() => import('@/routes/trainings'), 'TrainingsPage');
const TrainingNewPage = page(() => import('@/routes/training-edit'), 'TrainingNewPage');
const TrainingEditPage = page(() => import('@/routes/training-edit'), 'TrainingEditPage');
const TrainingRunPage = page(() => import('@/routes/training-run'), 'TrainingRunPage');
const TrainingDetailPage = page(() => import('@/routes/training-detail'), 'TrainingDetailPage');
const PlatformPage = page(() => import('@/routes/platform'), 'PlatformPage');

function Loading() {
  return (
    <p role="status" className="p-4 text-muted-foreground">
      Laden…
    </p>
  );
}

/** Alleen voor ingelogde gebruikers; anders de landingspagina (/) of inloggen met terugkeer. */
function Protected({ children }: { children: ReactNode }) {
  const { data, isPending } = authClient.useSession();
  const location = useLocation();
  if (isPending) return <Loading />;
  if (!data) {
    // Bezoekers zonder account zien op / de landingspagina in plaats van een redirect.
    if (location.pathname === '/') return <LandingPage />;
    const here = location.pathname + location.search;
    return <Navigate to={`/inloggen${here === '/' ? '' : nextQuery(here)}`} replace />;
  }
  return children;
}

/** Alleen voor uitgelogde gebruikers; ingelogd ga je naar `volgende` of het dashboard. */
function GuestOnly({ children }: { children: ReactNode }) {
  const { data, isPending } = authClient.useSession();
  const [params] = useSearchParams();
  if (isPending) return <Loading />;
  if (data) return <Navigate to={safeNextPath(params.get('volgende')) ?? '/'} replace />;
  return children;
}

export function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route
          element={
            <Protected>
              <AppShell />
            </Protected>
          }
        >
          <Route index element={<DashboardPage />} />
          <Route path="oefeningen" element={<ExercisesPage />} />
          <Route path="oefeningen/nieuw" element={<ExerciseNewPage />} />
          <Route path="oefeningen/video" element={<ExerciseVideoPage />} />
          <Route path="oefeningen/:id" element={<ExerciseDetailPage />} />
          <Route path="oefeningen/:id/bewerken" element={<ExerciseEditPage />} />
          <Route path="trainingen" element={<TrainingsPage />} />
          <Route path="trainingen/nieuw" element={<TrainingNewPage />} />
          <Route path="trainingen/:id" element={<TrainingDetailPage />} />
          <Route path="trainingen/:id/bewerken" element={<TrainingEditPage />} />
          <Route path="moderatie" element={<ModerationPage />} />
          <Route path="moderatie/:id" element={<ModerationReviewPage />} />
          <Route path="club" element={<ClubPage />} />
          <Route path="platform" element={<PlatformPage />} />
          <Route path="uitnodiging/:id" element={<InvitationPage />} />
        </Route>
        {/* Trainingsmodus (D-036): volledig scherm, zonder app-shell. */}
        <Route
          path="/trainingen/:id/start"
          element={
            <Protected>
              <Suspense fallback={<Loading />}>
                <TrainingRunPage />
              </Suspense>
            </Protected>
          }
        />
        <Route
          path="/inloggen"
          element={
            <GuestOnly>
              <LoginPage />
            </GuestOnly>
          }
        />
        <Route
          path="/registreren"
          element={
            <GuestOnly>
              <RegisterPage />
            </GuestOnly>
          }
        />
        {/* Zowel in- als uitgelogd bruikbaar: de link kan op een gedeeld apparaat openen. */}
        <Route path="/wachtwoord-instellen" element={<ResetPasswordPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
